import { useMemo } from 'react';
import { 
  Box,
  Typography,
  Divider 
} from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { BuzzvilDetailMetricsData } from '../../../types/buzzvil.types';

type Props = {
  data: BuzzvilDetailMetricsData[];
};

export default function BuzzvilDashboard({
  data,
}: Props) {

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  // 날짜별 총 매출 계산
  const dailyRevenue = useMemo(() => {
    const grouped = data.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = {
          date: item.date,
          revenue: 0
        };
      }
      acc[item.date].revenue += item.revenue;
      return acc;
    }, {} as Record<string, { date: string; revenue: number }>);

    return Object.values(grouped)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const totalRevenue = data.reduce((acc, curr) => acc + curr.revenue, 0);
  const totalCost = data.reduce((acc, curr) => acc + curr.cost, 0);
  const profit = totalRevenue - totalCost;

  return (
    <Box sx={{ display: 'flex', gap: 3, mt: 3, minWidth: 0 }}>
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          backgroundColor: '#e9edf5',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 2,
          minHeight: 100,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            총 매출
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="#0044D2">
            {formatCurrency(totalRevenue)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: 2
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
              총 비용
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formatCurrency(totalCost)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              순수익
            </Typography>
            <Typography 
              variant="body1" 
              fontWeight="bold"
            >
              {formatCurrency(profit)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
          borderRadius: 2,
          p: 3,
          flex: 1,
          minHeight: 100,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
          일별 매출 추이
        </Typography>
        <ResponsiveContainer width="100%" height={150}>
          <AreaChart data={dailyRevenue}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0044D2" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0044D2" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(value: string) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis tickFormatter={(value: number) => `${(value / 1000).toFixed(0)}K`} />
            <RechartsTooltip
              formatter={(value: number) => formatCurrency(value)}
              labelFormatter={(value: string) =>
                new Date(value).toLocaleDateString('ko-KR')
              }
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#0044D2"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
              name="매출"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
